package com.sweetlime.bingeamigo.repository.impl

import com.sweetlime.bingeamigo.model.ContentSummary
import com.sweetlime.bingeamigo.model.UserReview
import com.sweetlime.bingeamigo.repository.ReviewRepository
import dev.gitlive.firebase.firestore.FirebaseFirestore
import dev.gitlive.firebase.firestore.Timestamp
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.map

class ReviewRepositoryImpl(
    private val firestore: FirebaseFirestore
) : ReviewRepository {

    private fun reviewDocId(uid: String, contentType: String, contentId: String) =
        "${uid}_${contentType}_${contentId}".replace(":", "-").replace("/", "-")

    private fun contentDocId(contentType: String, contentId: String) =
        "${contentType}_${contentId}".replace(":", "-").replace("/", "-")

    override suspend fun submitReview(review: UserReview, previousRating: Double?) {
        // Only the review document is written from the client; the content rating
        // aggregate is maintained server-side by the onReview* Cloud Function
        // triggers, so clients can't forge averages/counts. `previousRating` is no
        // longer needed here (the trigger diffs before/after).
        val reviewRef = firestore.collection("reviews").document(
            reviewDocId(review.uid, review.contentType, review.contentId)
        )
        runCatching { reviewRef.set(review) }
    }

    override suspend fun getContentSummaries(docIds: List<String>): Map<String, ContentSummary> =
        runCatching {
            coroutineScope {
                docIds.distinct().map { docId ->
                    async {
                        val snap = runCatching {
                            firestore.collection("content").document(docId).get()
                        }.getOrNull()
                        docId to (runCatching { snap?.takeIf { it.exists }?.data<ContentSummary>() }.getOrNull() ?: ContentSummary())
                    }
                }.awaitAll().toMap()
            }
        }.getOrDefault(emptyMap())

    override suspend fun getUserReview(uid: String, contentType: String, contentId: String): UserReview? =
        runCatching {
            val docId = reviewDocId(uid, contentType, contentId)
            val snap = firestore.collection("reviews").document(docId).get()
            snap.takeIf { it.exists }?.data<UserReview>()?.copy(docId = docId)
        }.getOrNull()

    override suspend fun getFollowingReviews(
        contentType: String,
        contentId: String,
        followingIds: List<String>
    ): List<UserReview> = runCatching {
        followingIds.take(50).mapNotNull { uid ->
            runCatching {
                val docId = reviewDocId(uid, contentType, contentId)
                firestore.collection("reviews").document(docId).get()
                    .takeIf { it.exists }?.data<UserReview>()?.copy(docId = docId)
            }.getOrNull()
        }
    }.getOrDefault(emptyList())

    override suspend fun toggleLike(
        reviewDocId: String,
        likerUid: String,
        reviewOwnerUid: String
    ): Boolean {
        val reviewRef = firestore.collection("reviews").document(reviewDocId)
        val likeRef = reviewRef.collection("likes").document(likerUid)
        var liked = false

        runCatching {
            firestore.runTransaction {
                val likeDoc = get(likeRef)
                if (likeDoc.exists) {
                    delete(likeRef)
                    val current = runCatching { get(reviewRef).data<UserReview>().likeCount }.getOrDefault(0)
                    update(reviewRef, "likeCount" to maxOf(0, current - 1))
                    liked = false
                } else {
                    set(likeRef, mapOf("uid" to likerUid, "timestamp" to Timestamp.now()))
                    val current = runCatching { get(reviewRef).data<UserReview>().likeCount }.getOrDefault(0)
                    update(reviewRef, "likeCount" to (current + 1))
                    liked = true
                }
            }
        }

        // Reaction XP for the review author is awarded server-side by the
        // onLikeCreated Cloud Function trigger (capped + idempotent).
        return liked
    }

    override suspend fun getLikedReviewIds(currentUid: String, reviewDocIds: List<String>): Set<String> =
        runCatching {
            reviewDocIds.filter { docId ->
                runCatching {
                    firestore.collection("reviews").document(docId)
                        .collection("likes").document(currentUid)
                        .get().exists
                }.getOrDefault(false)
            }.toSet()
        }.getOrDefault(emptySet())

    override fun getContentSummary(contentType: String, contentId: String): Flow<ContentSummary> =
        firestore.collection("content")
            .document(contentDocId(contentType, contentId))
            .snapshots()
            .map { snap ->
                runCatching { snap.takeIf { it.exists }?.data<ContentSummary>() }.getOrNull()
                    ?: ContentSummary()
            }
            .catch { emit(ContentSummary()) }
}